/**
 * useLineage — fetches the public-readable subset of all Orchists for the
 * Acknowledged list + Chain views.
 *
 * The RLS policy `public_read_orchist_row` exposes only rows where
 * isOrchist=true, and only the columns we select here. Each Orchist's
 * private record (real name, internal IDs, etc.) is never reachable
 * through this hook regardless of caller's auth state.
 *
 * Returns rows ordered chronologically by invitationConfirmedAt
 * (Acknowledgment time), with The First (no inviter) at the top.
 *
 * Phase G.7.7 — Pagination:
 *   The hook now supports cursor-based pagination via `pageSize` (default
 *   50). Returns `loadMore()` and `hasMore` so consumers can implement
 *   infinite-scroll or "load more" buttons. The full-population case
 *   (Stage A: <1k Orchists) still works fine — the first page covers it.
 *
 * @scale-marker stage-a   — Sovereign Stewardship will email when count > 1k.
 *   At that point, the cursor in `useLineage` becomes the load-bearing path:
 *   replace the all-rows fetch with strict pageSize=50 (or 100) cursor
 *   loops; LineageList must add react-virtual or similar for visible-row
 *   rendering. ChainTree migrates separately to subtree fetch.
 * @scale-marker stage-b   — At ~100k, replace the chronological cursor with
 *   server-side time-bucket aggregation (`/lineage_by_quarter`); individual
 *   rows visible only inside the expanded bucket.
 * @scale-marker stage-c   — At ~100M, viewport-based fetching by spatial
 *   tile; chronological view becomes era-locked archive download.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { utcToLocalDateString, resolveTimezone } from '@/lib/dates';
import { useAuth } from '@/contexts/AuthContext';
import type { OrchistIdentityMode } from '@orchist/types';

export interface LineageRow {
  id: string;
  publicIdentityMode: OrchistIdentityMode | null;
  publicIdentityString: string | null;
  glyphSeed: string | null;
  invitationSentBy: string | null;
  invitationSentAt: string | null;
  invitationAcceptedAt: string | null;
  invitationConfirmedAt: string | null;
  /** Each Orchist's own configured timezone (E-002 Rule 5). May be null. */
  timezone: string | null;
  /**
   * Free-Will Doctrine: when not null, this Orchist has set down the
   * recognition. The row remains in The Line; the public view shows them
   * in a dimmer "those who released" section, not the active body.
   */
  orchistDisownedAt: string | null;
  /**
   * Phase G.7.10: when true, the inviter's identity is hidden in public
   * display ("By an anonymous Orchist") and on the invitee's /acknowledge
   * surface ("by an Orchist who chose to remain anonymous"). The DB still
   * records invitationSentBy for audit; the display layer honors the
   * choice. Per the 2026-04-26 amendment, anonymity also suppresses the
   * inviter→invitee email share row + change-request email naming
   * across both directions (see sacred/anonymous-invitations.md §VIII).
   */
  invitationAnonymous: boolean;
}

interface UseLineageOptions {
  /** Rows per page. Defaults to 50. */
  pageSize?: number;
}

interface UseLineageState {
  loading: boolean;
  /** Loaded rows so far (across all loaded pages). */
  rows: LineageRow[];
  /** Total active acknowledged count from the server. */
  totalCount: number | null;
  hasMore: boolean;
  loadingMore: boolean;
  error: Error | null;
}

const LINEAGE_COLUMNS =
  'id, publicIdentityMode, publicIdentityString, glyphSeed, invitationSentBy, invitationSentAt, invitationAcceptedAt, invitationConfirmedAt, timezone, orchistDisownedAt, invitationAnonymous';

const DEFAULT_PAGE_SIZE = 50;

export function useLineage(options: UseLineageOptions = {}): UseLineageState & {
  loadMore: () => Promise<void>;
  refresh: () => void;
} {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const { contact } = useAuth();
  const [state, setState] = useState<UseLineageState>({
    loading: true,
    rows: [],
    totalCount: null,
    hasMore: false,
    loadingMore: false,
    error: null,
  });
  const [page, setPage] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setPage(0);
    setRefreshKey((k) => k + 1);
  }, []);

  // Re-fetch on viewer's identity change OR explicit refresh
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, rows: [], error: null }));

      // Page 0: fetch first batch + the total count
      const from = 0;
      const to = pageSize - 1;
      const { data, error, count } = await supabase
        .schema('core')
        .from('Contact')
        .select(LINEAGE_COLUMNS, { count: 'exact' })
        .eq('isOrchist', true)
        .order('invitationConfirmedAt', { ascending: true, nullsFirst: true })
        .order('invitationAcceptedAt', { ascending: true, nullsFirst: true })
        .range(from, to);

      if (cancelled) return;
      if (error) {
        setState({
          loading: false,
          rows: [],
          totalCount: null,
          hasMore: false,
          loadingMore: false,
          error: new Error(error.message),
        });
        return;
      }

      const rows = (data ?? []) as LineageRow[];
      setState({
        loading: false,
        rows,
        totalCount: count ?? rows.length,
        hasMore: (count ?? rows.length) > rows.length,
        loadingMore: false,
        error: null,
      });
      setPage(1);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contact?.id,
    contact?.publicIdentityMode,
    contact?.publicIdentityString,
    contact?.glyphSeed,
    contact?.orchistDisownedAt,
    refreshKey,
    pageSize,
  ]);

  const loadMore = useCallback(async () => {
    if (state.loadingMore || !state.hasMore) return;
    setState((s) => ({ ...s, loadingMore: true }));

    const from = page * pageSize;
    const to = from + pageSize - 1;
    const { data, error } = await supabase
      .schema('core')
      .from('Contact')
      .select(LINEAGE_COLUMNS)
      .eq('isOrchist', true)
      .order('invitationConfirmedAt', { ascending: true, nullsFirst: true })
      .order('invitationAcceptedAt', { ascending: true, nullsFirst: true })
      .range(from, to);

    if (error) {
      setState((s) => ({ ...s, loadingMore: false, error: new Error(error.message) }));
      return;
    }

    const newRows = (data ?? []) as LineageRow[];
    setState((s) => {
      const combined = [...s.rows, ...newRows];
      return {
        ...s,
        rows: combined,
        hasMore: s.totalCount ? combined.length < s.totalCount : newRows.length === pageSize,
        loadingMore: false,
      };
    });
    setPage((p) => p + 1);
  }, [page, pageSize, state.hasMore, state.loadingMore, state.totalCount]);

  return { ...state, loadMore, refresh };
}

/**
 * Helper: render the public-facing display string for a LineageRow,
 * respecting the Orchist's chosen identity mode. Dates rendered in the
 * VIEWER's configured timezone (E-002 Rule 5: configured, not implicit).
 *
 *   NAMED    → the chosen name verbatim
 *   ALIASED  → the chosen alias verbatim
 *   GENERIC  → "Orchist · YYYY-MM-DD"  (glyph rendered separately by caller)
 */
export function lineageDisplayString(row: LineageRow, viewerTimezone?: string | null): string {
  if (row.publicIdentityMode === 'GENERIC') {
    const tz = resolveTimezone(viewerTimezone);
    const date = utcToLocalDateString(row.invitationAcceptedAt ?? row.invitationConfirmedAt, tz);
    return date ? `Orchist · ${date}` : 'Orchist';
  }
  return row.publicIdentityString ?? 'Orchist';
}

/** Helper: is this row The First (self-recognized, no inviter)? */
export function isFirstOrchist(row: LineageRow): boolean {
  return row.invitationSentBy === null;
}

/**
 * Free-Will Doctrine: an Orchist who has set down the recognition.
 * Their row remains in The Line; views render them in a dimmer
 * "those who released" section instead of the active body.
 */
export function isDisowned(row: LineageRow): boolean {
  return row.orchistDisownedAt !== null;
}

/** Split rows into active vs disowned, preserving the original chronological order. */
export function splitByDisownment(rows: LineageRow[]): {
  active: LineageRow[];
  disowned: LineageRow[];
} {
  const active: LineageRow[] = [];
  const disowned: LineageRow[] = [];
  for (const row of rows) {
    if (isDisowned(row)) {
      disowned.push(row);
    } else {
      active.push(row);
    }
  }
  return { active, disowned };
}
